import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Alert,
  IconButton,
  Stack
} from '@mui/joy';
import {
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import { Add, Edit, Delete, Schedule } from '@mui/icons-material';
import { PreferenceService } from '@/services/preferenceService';
import { useAuth } from '@/context/AuthContext';
import {
  DayPreference,
  CreateDayPreferenceData,
  UpdateDayPreferenceData,
  DAY_PREFERENCE_TYPES,
  DAYS_OF_WEEK,
  PRIORITY_OPTIONS
} from '@/types/preference';

interface DayPreferencesTabProps {
  preferences: DayPreference[];
  onUpdate: () => void;
}

const DayPreferencesTab: React.FC<DayPreferencesTabProps> = ({
  preferences,
  onUpdate
}) => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPreference, setEditingPreference] = useState<DayPreference | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateDayPreferenceData>({
    employee_id: user?.id || 0,
    day_of_week: 0,
    preference_type: 'neutral',
    time_restriction_start: '',
    time_restriction_end: '',
    week_of_month: undefined,
    reason: '',
    priority: 'medium'
  });

  const handleOpenDialog = (preference?: DayPreference) => {
    if (preference) {
      setEditingPreference(preference);
      setFormData({
        employee_id: preference.employee_id,
        day_of_week: preference.day_of_week,
        preference_type: preference.preference_type,
        time_restriction_start: preference.time_restriction_start || '',
        time_restriction_end: preference.time_restriction_end || '',
        week_of_month: preference.week_of_month,
        reason: preference.reason || '',
        priority: preference.priority
      });
    } else {
      setEditingPreference(null);
      setFormData({
        employee_id: user?.id || 0,
        day_of_week: 0,
        preference_type: 'neutral',
        time_restriction_start: '',
        time_restriction_end: '',
        week_of_month: undefined,
        reason: '',
        priority: 'medium'
      });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPreference(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const errors = PreferenceService.validateDayPreference(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }

      if (editingPreference) {
        await PreferenceService.updateDayPreference(editingPreference.id, formData);
      } else {
        await PreferenceService.createDayPreference(formData);
      }

      handleCloseDialog();
      onUpdate();
    } catch (err) {
      console.error('Error saving day preference:', err);
      setError('Failed to save preference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (preference: DayPreference) => {
    if (window.confirm('Are you sure you want to delete this preference?')) {
      try {
        await PreferenceService.deleteDayPreference(preference.id);
        onUpdate();
      } catch (err) {
        console.error('Error deleting preference:', err);
        setError('Failed to delete preference. Please try again.');
      }
    }
  };

  const getPreferenceTypeColor = (type: string) => {
    switch (type) {
      case 'unavailable': return 'danger';
      case 'preferred': return 'success';
      case 'neutral': return 'neutral';
      default: return 'neutral';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'neutral';
    }
  };

  const getDayName = (dayOfWeek: number) => {
    return DAYS_OF_WEEK.find(day => day.value === dayOfWeek)?.label || 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h4">
          Day Preferences
        </Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => handleOpenDialog()}
          variant="solid"
        >
          Add Day Preference
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preferences.length === 0 ? (
        <Card variant="soft">
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Schedule sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
              <Typography level="title-sm" color="neutral">
                No day preferences set
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Add day preferences to set your availability for specific days of the week.
              </Typography>
              <Button onClick={() => handleOpenDialog()}>
                Add Your First Day Preference
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {preferences.map((preference) => (
            <Grid xs={12} md={6} lg={4} key={preference.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography level="title-sm">
                        {getDayName(preference.day_of_week)}
                      </Typography>
                      {preference.week_of_month && (
                        <Typography level="body-sm" color="neutral">
                          Week {preference.week_of_month} of month
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => handleOpenDialog(preference)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(preference)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      size="sm"
                      color={getPreferenceTypeColor(preference.preference_type)}
                    >
                      {preference.preference_type}
                    </Chip>
                    <Chip
                      size="sm"
                      color={getPriorityColor(preference.priority)}
                      variant="soft"
                    >
                      {preference.priority}
                    </Chip>
                  </Stack>

                  {preference.time_restriction_start && preference.time_restriction_end && (
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      <strong>Time:</strong> {preference.time_restriction_start} - {preference.time_restriction_end}
                    </Typography>
                  )}

                  {preference.reason && (
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      <strong>Reason:</strong> {preference.reason}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Modal */}
      <Modal open={openDialog} onClose={handleCloseDialog}>
        <ModalDialog size="sm">
          <ModalClose />
          <Typography level="title-sm" component="h2">
            {editingPreference ? 'Edit Day Preference' : 'Add Day Preference'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl>
              <FormLabel>Day of Week</FormLabel>
              <Select
                value={formData.day_of_week}
                onChange={(e, value) => setFormData({ ...formData, day_of_week: value || 0 })}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <Option key={day.value} value={day.value}>
                    {day.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Preference Type</FormLabel>
              <Select
                value={formData.preference_type}
                onChange={(e, value) => setFormData({ ...formData, preference_type: value || 'neutral' })}
              >
                {DAY_PREFERENCE_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e, value) => setFormData({ ...formData, priority: value || 'medium' })}
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <Option key={priority.value} value={priority.value}>
                    {priority.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Week of Month (Optional)</FormLabel>
              <Select
                value={formData.week_of_month || ''}
                onChange={(e, value) => setFormData({ ...formData, week_of_month: value ? parseInt(String(value)) : undefined })}
              >
                <Option value="">All weeks</Option>
                <Option value="1">Week 1</Option>
                <Option value="2">Week 2</Option>
                <Option value="3">Week 3</Option>
                <Option value="4">Week 4</Option>
                <Option value="5">Week 5</Option>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl>
                <FormLabel>Start Time (Optional)</FormLabel>
                <Input
                  type="time"
                  value={formData.time_restriction_start}
                  onChange={(e) => setFormData({ ...formData, time_restriction_start: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>End Time (Optional)</FormLabel>
                <Input
                  type="time"
                  value={formData.time_restriction_end}
                  onChange={(e) => setFormData({ ...formData, time_restriction_end: e.target.value })}
                />
              </FormControl>
            </Box>

            <FormControl>
              <FormLabel>Reason (Optional)</FormLabel>
              <Textarea
                minRows={2}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Explain why you have this preference..."
              />
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
            <Button variant="plain" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {editingPreference ? 'Update' : 'Create'}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default DayPreferencesTab; 